import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { StyledCard } from 'components/Custom/card';
import { TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH } from './tax-rates-promotion-banner';

const SIDE_PANEL_WIDTH = TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const Bone = styled.div<{
  $width?: number | string;
  $height?: number;
  $radius?: number | string;
  $mb?: number;
  $flexShrink?: number;
}>`
  width: ${(p) =>
    typeof p.$width === 'number' ? `${p.$width}px` : p.$width ?? '100%'};
  height: ${(p) => p.$height ?? 14}px;
  margin-bottom: ${(p) => p.$mb ?? 0}px;
  flex-shrink: ${(p) => p.$flexShrink ?? 0};
  border-radius: ${(p) => p.$radius ?? 4}px;
  background: linear-gradient(90deg, #eef2f2 25%, #e2eaea 50%, #eef2f2 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const StatColumn = styled.div`
  border-left: 1px solid var(--color-primary, #41aaaf);
  padding-left: 10px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DropZoneShell = styled.div`
  flex: 1.2;
  min-width: 0;
  min-height: 220px;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: #fafafa;
  gap: 8px;
`;

const ScanningPrefsShell = styled.div`
  flex: 1;
  min-width: min(100%, 300px);
  max-width: 420px;
  border-radius: 10px;
  border: 1px solid var(--color-border, #dbeae3);
  background: var(--color-surface, #f3f7f8);
  padding: 20px;
  box-sizing: border-box;
`;

const SidePanelCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  background-color: var(--color-surface-raised, #ffffff);
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 10px;
  box-shadow: 0 0 10px 0 #0000000d;
  padding: 24px;
`;

const SidePanelDivider = styled.div`
  height: 1px;
  background: var(--color-border, #dbeae3);
  margin: 16px 0;
`;

const MetaField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PreferenceRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--color-border, #dbeae3);
`;

const MultiCurrencyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const UploadStatsRow = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 20,
      columnGap: 50,
      marginBottom: 16,
      width: '100%',
    }}
  >
    <StatColumn style={{ minWidth: 200, maxWidth: 260, flex: '1 1 200px' }}>
      <Bone $width="72%" $height={12} />
      <Bone $width="88%" $height={18} />
    </StatColumn>
    <StatColumn style={{ minWidth: 90, maxWidth: 120, flex: '0 1 110px' }}>
      <Bone $width="85%" $height={12} />
      <Bone $width={28} $height={18} />
    </StatColumn>
    <StatColumn style={{ minWidth: 120, maxWidth: 150, flex: '0 1 140px' }}>
      <Bone $width="70%" $height={12} />
      <Bone $width={96} $height={18} />
    </StatColumn>
    <StatColumn style={{ minWidth: 180, maxWidth: 260, flex: '1 1 200px' }}>
      <Bone $width="65%" $height={12} />
      <Bone $width="92%" $height={18} />
    </StatColumn>
    <StatColumn style={{ minWidth: 140, flex: '1 1 140px' }}>
      <Bone $width="80%" $height={12} />
      <Bone $width={96} $height={18} />
    </StatColumn>
  </div>
);

const UploadTitleRow = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 12,
      marginBottom: 48,
      width: '100%',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <Bone $width={20} $height={20} $radius={4} />
      <Bone $width={220} $height={24} $radius={4} />
      <Bone $width={52} $height={22} $radius={4} />
    </div>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 12px',
        border: '1px solid var(--color-border, #dbeae3)',
        borderRadius: 20,
        background: 'var(--color-surface-raised, #fff)',
      }}
    >
      <Bone $width={16} $height={16} $radius="50%" />
      <Bone $width={88} $height={14} />
    </div>
  </div>
);

const DropZoneSkeleton = () => (
  <DropZoneShell>
    <Bone $width={80} $height={80} $radius={12} $mb={16} />
    <Bone $width="72%" $height={14} $mb={8} />
    <Bone $width="48%" $height={12} />
  </DropZoneShell>
);

const ScanningPreferencesSkeleton = () => (
  <ScanningPrefsShell>
    <Bone $width={168} $height={20} $mb={20} />

    <PreferenceRow>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Bone $width="78%" $height={16} $mb={8} />
        <Bone $width="100%" $height={12} $mb={6} />
        <Bone $width="92%" $height={12} />
      </div>
      <Bone $width={44} $height={22} $radius={11} $flexShrink={0} />
    </PreferenceRow>

    <MultiCurrencyRow>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Bone $width="85%" $height={16} $mb={8} />
        <Bone $width="55%" $height={12} />
      </div>
      <Bone $width={12} $height={12} $radius={2} $flexShrink={0} />
    </MultiCurrencyRow>
  </ScanningPrefsShell>
);

const FolderCaptureCardSkeleton = () => (
  <SidePanelCard>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
      }}
    >
      <Bone $width="72%" $height={22} />
      <Bone $width={18} $height={18} $radius="50%" />
    </div>

    <SidePanelDivider />

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 20,
      }}
    >
      <Bone $width={28} $height={28} $radius={6} $flexShrink={0} />
      <Bone $width={100} $height={16} $flexShrink={0} />
      <Bone $width={58} $height={22} $radius={4} $flexShrink={0} />
    </div>

    <MetaField>
      <Bone $width={96} $height={12} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Bone $width="78%" $height={16} />
        <Bone $width={18} $height={18} $radius={4} $flexShrink={0} />
      </div>
    </MetaField>

    <MetaField>
      <Bone $width={88} $height={12} />
      <Bone $width="90%" $height={16} />
    </MetaField>

    <MetaField>
      <Bone $width={92} $height={12} />
      <Bone $width={112} $height={16} />
    </MetaField>
  </SidePanelCard>
);

const EmailCaptureCardSkeleton = () => (
  <SidePanelCard>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
      }}
    >
      <Bone $width="68%" $height={22} />
      <Bone $width={18} $height={18} $radius="50%" />
    </div>

    <SidePanelDivider />

    <Bone $width="100%" $height={12} $mb={6} />
    <Bone $width="88%" $height={12} $mb={16} />

    <MetaField>
      <Bone $width={40} $height={12} />
      <Bone $width="95%" $height={16} />
    </MetaField>

    <MetaField>
      <Bone $width={88} $height={12} />
      <Bone $width="85%" $height={16} />
    </MetaField>

    <MetaField>
      <Bone $width={92} $height={12} />
      <Bone $width={112} $height={16} />
    </MetaField>
  </SidePanelCard>
);

const MainUploadCardSkeleton = () => (
  <StyledCard
    style={{
      width: '100%',
      maxWidth: 1250,
      padding: '24px',
      fontSize: 14,
      fontWeight: 400,
    }}
  >
    <UploadTitleRow />
    <UploadStatsRow />

    <div
      style={{
        borderTop: '1px solid var(--color-border, #dbeae3)',
        margin: '40px 0',
      }}
    />

    <Bone $width="98%" $height={14} $mb={8} />
    <Bone $width="94%" $height={14} $mb={8} />
    <Bone $width="76%" $height={14} $mb={24} />

    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 20,
        width: '100%',
        marginBottom: 12,
      }}
    >
      <DropZoneSkeleton />
      <ScanningPreferencesSkeleton />
    </div>

    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        marginTop: 4,
        width: '100%',
      }}
    >
      <Bone $width="88%" $height={11} />
      <Bone $width="72%" $height={11} />
    </div>
  </StyledCard>
);

export type CaptureTabSkeletonLayout = 'with-side-panel' | 'main-only';

export type CaptureTabSkeletonProps = {
  layout?: CaptureTabSkeletonLayout;
  suppressOuterTopPadding?: boolean;
};

export const CaptureTabSkeleton: React.FC<CaptureTabSkeletonProps> = ({
  layout = 'with-side-panel',
  suppressOuterTopPadding = layout === 'with-side-panel',
}) => {
  const { t } = useTranslation();
  const mainColumn = (
    <div
      style={{
        paddingTop: suppressOuterTopPadding ? 0 : 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <MainUploadCardSkeleton />
    </div>
  );

  if (layout === 'main-only') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {mainColumn}
      </div>
    );
  }

  return (
    <div
      className="entity-capture-tab entity-capture-tab--with-inline-folder"
      style={{ width: '100%' }}
      aria-busy
      aria-label={t('organisation.loading-capture-documents')}
    >
      <div className="capture-tab-wrapper">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            gap: 20,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ flex: 1, minWidth: 600 }}>{mainColumn}</div>
          <div
            style={{
              width: SIDE_PANEL_WIDTH,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              paddingTop: 0,
            }}
          >
            <FolderCaptureCardSkeleton />
            <EmailCaptureCardSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
};
